import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Select, SelectItem } from "@heroui/react";
import { Icon } from "@iconify/react";
import { createForm } from "src/components/forms/createForm";
import { serviceLevelLabels, TRequest, TServiceLevel } from "@shared/entities";
import { useServicePricing } from "src/hooks/useServicePricing";

const Form = createForm<TRequest>();

/** Admin-only part of the request form: service level and price. The client
 *  never sees either. Picking a level fills in that level's price (editable);
 *  an empty price on a new request takes the level's price on creation. */
export function AdminServiceFields() {
	const { control, setValue } = useFormContext<TRequest>();
	const level = (useWatch<TRequest>({ name: "serviceLevel" }) as TServiceLevel | undefined) ?? "full";
	const { pricing } = useServicePricing();
	const levelPrice = pricing?.[level];

	return (
		<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
			<Controller
				name="serviceLevel"
				control={control}
				render={({ field }) => (
					<Select
						label="רמת שירות"
						startContent={<Icon icon="lucide:layers" className="h-4 w-4 text-default-400" />}
						selectedKeys={[field.value ?? "full"]}
						onSelectionChange={(keys) => {
							const next = Array.from(keys)[0] as TServiceLevel | undefined;
							if (!next || next === field.value) return;
							field.onChange(next);
							// Only on the admin's own pick, never on loading the request:
							// the price follows the level; no price → none.
							setValue("paymentDetails.cost", pricing?.[next] ?? (undefined as never), { shouldDirty: true });
						}}
					>
						{(["full", "basic"] as const).map((value) => (
							<SelectItem key={value}>{serviceLevelLabels[value]}</SelectItem>
						))}
					</Select>
				)}
			/>
			<Form.Input
				name="paymentDetails.cost"
				type="number"
				label="עלות הבקשה"
				placeholder={levelPrice ? `${levelPrice} ₪ — מחיר ${serviceLevelLabels[level]}` : "לא נקבע — כל תשלום נחשב ״שולם״"}
				startContent={<Icon icon="lucide:shekel-sign" className="h-4 w-4 text-default-400" />}
			/>
		</div>
	);
}
